/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * TODO: GRAPHYTE-2511 wire up real behaviour for the "Copy to clipboard"
 * button (no `onClick` handler yet). The OS dropdown currently only
 * updates its own trigger label — the install commands rendered in
 * `TerminalBody` are OS-independent for now.
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import CopyIcon from '@atlaskit/icon/core/copy';
import { token } from '@atlaskit/tokens';

import { messages } from '../../messages';

import type { Os } from './types';
import { OS_LABELS, OS_OPTIONS } from './utils';

export type HeaderActionsProps = {
	/** Currently selected OS — drives the dropdown trigger label. */
	selectedOs: Os;
	/** Called when the user picks a different OS from the dropdown. */
	onSelectOs: (os: Os) => void;
};

const styles = cssMap({
	root: {
		display: 'inline-flex',
		alignItems: 'center',
		height: '36px',
		gap: token('space.100'),
	},
	button: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.075'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		backgroundColor: 'transparent',
		font: token('font.body'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			backgroundColor: 'rgba(255, 255, 255, 0.06)',
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			color: '#F0F1F2',
		},
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			outlineOffset: token('space.025'),
		},
	},
	buttonBordered: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#292A2E',
		'&:hover': {
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			borderColor: '#44474C',
		},
	},
});

export const HeaderActions = ({ selectedOs, onSelectOs }: HeaderActionsProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.root}>
			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, ...triggerProps }) => (
					<button
						{...triggerProps}
						ref={triggerRef}
						type="button"
						aria-label={formatMessage(messages.terminalOsDropdownAriaLabel)}
						css={styles.button}
					>
						{OS_LABELS[selectedOs]}
						<ChevronDownIcon label="" color="currentColor" />
					</button>
				)}
				shouldRenderToParent
				placement="bottom-start"
			>
				<DropdownItemGroup>
					{OS_OPTIONS.map((os) => (
						<DropdownItem key={os} isSelected={os === selectedOs} onClick={() => onSelectOs(os)}>
							{OS_LABELS[os]}
						</DropdownItem>
					))}
				</DropdownItemGroup>
			</DropdownMenu>
			<button
				type="button"
				aria-label={formatMessage(messages.copyToClipboardAriaLabel)}
				css={[styles.button, styles.buttonBordered]}
			>
				<FormattedMessage {...messages.copyToClipboard} />
				<CopyIcon label="" color="currentColor" />
			</button>
		</div>
	);
};
